import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Box,
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Stack,
  Card,
  CardContent,
  alpha,
  useTheme,
  Tooltip,
  Collapse,
} from '@mui/material'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Code,
  Sliders,
  Clock,
  Hash,
  Info,
  ChevronDown,
  CheckCircle,
  AlertCircle,
} from 'lucide-react'
import { CronExpressionParser } from 'cron-parser'
import cronstrue from 'cronstrue/i18n'

const ModernCronScheduleEditor = ({ value, setFieldValue, error, plan }) => {
  const theme = useTheme()
  
  // UI States
  const [mode, setMode] = useState('visual')
  const [showHelp, setShowHelp] = useState(false)
  const [internalValue, setInternalValue] = useState(value)
  
  // Visual mode fields
  const [fields, setFields] = useState({
    minute: '*',
    hour: '*',
    dayOfMonth: '*',
    month: '*',
    dayOfWeek: '*'
  })

  // Refs
  const isInitialMount = useRef(true)
  const isInternalUpdate = useRef(false)

  // Quick presets
  const presets = [
    // `=== 1` idi: alt sınırı 1'den küçük (daha cömert) planlarda bu ön ayar
    // yanlışlıkla kilitli görünüyordu.
    { label: 'Her Dakika', value: '* * * * *', access: Number(plan.limits.min_interval) <= 1 },
    { label: 'Her 5 Dakika', value: '*/5 * * * *', access: true },
    { label: 'Her 10 Dakika', value: '*/10 * * * *', access: true },
    { label: 'Her 30 Dakika', value: '*/30 * * * *', access: true },
    { label: 'Her Saat', value: '0 * * * *', access: true },
    { label: 'Her Gün 00:00', value: '0 0 * * *', access: true },
    { label: 'Her Pazartesi', value: '0 0 * * 1', access: true },
  ]

  // Validation using cron-parser
  const validateCron = useCallback((expr) => {
    if (!expr || typeof expr !== 'string') {
      return { valid: false, error: 'Cron ifadesi boş olamaz' }
    }

    const trimmed = expr.trim()
    const parts = trimmed.split(/\s+/)

    if (parts.length !== 5) {
      return { 
        valid: false, 
        error: `5 alan bekleniyor, ${parts.length} alan bulundu` 
      }
    }

    // Check day of month and day of week conflict
    const [, , dayOfMonth, , dayOfWeek] = parts
    if (dayOfMonth !== '*' && dayOfWeek !== '*') {
      return {
        valid: false,
        error: 'Ayın günü ve haftanın günü aynı anda belirtilemez. Birini "*" yapın'
      }
    }

    // Use cron-parser library
    try {
      CronExpressionParser.parse(trimmed, {
        tz: 'Europe/Istanbul'
      })
    } catch (error) {
      return { 
        valid: false, 
        error: error.message || 'Geçersiz cron ifadesi. Lütfen formatı kontrol edin.' 
      }
    }

    // Check plan interval limits
    const minute = parts[0].length === 1 ? 1 : Number(parts[0].split("/")[1]);

    if (minute < Number(plan.limits.min_interval)) {
      return { 
        valid: false, 
        error: 'Paket yükseltilmesi gerekiyor. Lütfen paketinizi güncelleyin.' 
      }
    }

    return { valid: true, error: null }
  }, [plan.limits.min_interval])

  // Get human-readable description
  const getCronDescription = useCallback((expr) => {
    const validation = validateCron(expr)
    if (!validation.valid) {
      return validation.error
    }

    try {
      // Use cronstrue for Turkish description
      const description = cronstrue.toString(expr, { 
        locale: 'tr',
        use24HourTimeFormat: true,
        throwExceptionOnParseError: false
      })
      return description
    } catch (error) {
      // Fallback to basic description
      return 'Geçerli cron ifadesi'
    }
  }, [validateCron])

  // Parse cron to visual fields
  const parseToVisual = useCallback((expr) => {
    const parts = expr.trim().split(/\s+/)
    if (parts.length === 5) {
      setFields({
        minute: parts[0],
        hour: parts[1],
        dayOfMonth: parts[2],
        month: parts[3],
        dayOfWeek: parts[4]
      })
    }
  }, [])

  // Build cron from visual fields
  const buildFromVisual = useCallback(() => {
    return `${fields.minute} ${fields.hour} ${fields.dayOfMonth} ${fields.month} ${fields.dayOfWeek}`
  }, [fields])

  // Handle external value changes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      parseToVisual(value)
      return
    }

    if (isInternalUpdate.current) {
      isInternalUpdate.current = false
      return
    }

    if (value && value !== internalValue) {
      setInternalValue(value)
      if (mode === 'visual') {
        parseToVisual(value)
      }
    }
  }, [value, internalValue, mode, parseToVisual])

  // Handle visual field changes
  useEffect(() => {
    if (mode === 'visual' && !isInitialMount.current) {
      const newCron = buildFromVisual()
      if (newCron !== internalValue) {
        isInternalUpdate.current = true
        setInternalValue(newCron)
        setFieldValue("cronSchedule", newCron)
      }
    }
  }, [fields, mode, buildFromVisual, internalValue, setFieldValue])

  // Handlers
  const handleModeChange = (_, newMode) => {
    if (newMode) {
      setMode(newMode)
      if (newMode === 'visual') {
        parseToVisual(internalValue)
      }
    }
  }

  const handleManualChange = (e) => {
    const newValue = e.target.value
    isInternalUpdate.current = true
    setInternalValue(newValue)
    setFieldValue("cronSchedule", newValue)
  }

  const handlePresetClick = (preset) => {
    if (!preset.access) return
    
    isInternalUpdate.current = true
    setInternalValue(preset.value)
    setFieldValue("cronSchedule", preset.value)
    
    if (mode === 'visual') {
      parseToVisual(preset.value)
    }
  }

  const handleFieldChange = (field, value) => {
    setFields(prev => {
      const newFields = { ...prev, [field]: value }
      
      // Handle day conflict
      if (field === 'dayOfMonth' && value !== '*') {
        newFields.dayOfWeek = '*'
      } else if (field === 'dayOfWeek' && value !== '*') {
        newFields.dayOfMonth = '*'
      }
      
      return newFields
    })
  }

  // Validation state
  const cronValidation = validateCron(internalValue)
  const isValid = cronValidation.valid

  return (
    <Card
      component={motion.div}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      sx={{
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.background.paper,
          0.8
        )}, ${alpha(theme.palette.background.paper, 0.6)})`,
        backdropFilter: 'blur(20px)',
        border: `1px solid ${alpha(
          error ? theme.palette.error.main : theme.palette.primary.main,
          0.2
        )}`,
        borderRadius: 2,
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        {/* Header */}
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          justifyContent="space-between" 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                background: alpha(theme.palette.primary.main, 0.1),
              }}
            >
              <Clock size={18} color={theme.palette.primary.main} />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Çalışma Zamanlaması (Cron)
            </Typography>
          </Stack>

          {/* Mode Toggle */}
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={handleModeChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                px: 1.5,
                py: 0.5,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                color: alpha('#fff', 0.7),
                fontSize: '0.75rem',
                fontWeight: 600,
                '&.Mui-selected': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  color: '#000',
                  border: 'none',
                },
              },
            }}
          >
            <ToggleButton value="visual">
              <Sliders size={14} style={{ marginRight: 4 }} />
              Görsel
            </ToggleButton>
            <ToggleButton value="manual">
              <Code size={14} style={{ marginRight: 4 }} />
              Manuel
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {/* Quick Presets */}
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="caption" sx={{ color: alpha('#fff', 0.6), mb: 1, display: 'block' }}>
            Hızlı Seçenekler
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
            {presets.map((preset, idx) => (
              <Chip
                key={idx}
                label={preset.label}
                onClick={() => handlePresetClick(preset)}
                disabled={!preset.access}
                variant={internalValue === preset.value ? 'filled' : 'outlined'}
                sx={{
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  color: internalValue === preset.value ? '#000' : alpha('#fff', 0.8),
                  background:
                    internalValue === preset.value
                      ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`
                      : 'transparent',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  opacity: preset.access ? 1 : 0.5,
                  cursor: preset.access ? 'pointer' : 'not-allowed',
                  '&:hover': {
                    borderColor: preset.access ? theme.palette.primary.main : undefined,
                    background:
                      preset.access && internalValue !== preset.value
                        ? alpha(theme.palette.primary.main, 0.1)
                        : undefined,
                  },
                }}
                icon={
                  !preset.access ? (
                    <Tooltip title="Premium özellik">
                      <AlertCircle size={14} />
                    </Tooltip>
                  ) : undefined
                }
              />
            ))}
          </Stack>
        </Box>

        <AnimatePresence mode="wait">
          {/* Visual Mode */}
          {mode === 'visual' && (
            <Box
              component={motion.div}
              key="visual"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              <Grid container spacing={2}>
                {/* Minute */}
                <Grid item xs={6} sm={4} md={2.4}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontSize: '0.875rem' }}>Dakika</InputLabel>
                    <Select
                      value={fields.minute}
                      onChange={(e) => handleFieldChange('minute', e.target.value)}
                      label="Dakika"
                      sx={{
                        background: alpha(theme.palette.background.default, 0.5),
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: alpha(theme.palette.primary.main, 0.2),
                        },
                      }}
                    >
                      <MenuItem value="*">Her dakika (*)</MenuItem>
                      <MenuItem value="*/5">Her 5 dk (*/5)</MenuItem>
                      <MenuItem value="*/10">Her 10 dk (*/10)</MenuItem>
                      <MenuItem value="*/15">Her 15 dk (*/15)</MenuItem>
                      <MenuItem value="*/30">Her 30 dk (*/30)</MenuItem>
                      <MenuItem value="0">0. dakika</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Hour */}
                <Grid item xs={6} sm={4} md={2.4}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontSize: '0.875rem' }}>Saat</InputLabel>
                    <Select
                      value={fields.hour}
                      onChange={(e) => handleFieldChange('hour', e.target.value)}
                      label="Saat"
                      sx={{
                        background: alpha(theme.palette.background.default, 0.5),
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: alpha(theme.palette.primary.main, 0.2),
                        },
                      }}
                    >
                      <MenuItem value="*">Her saat (*)</MenuItem>
                      {[...Array(24)].map((_, i) => (
                        <MenuItem key={i} value={i.toString()}>
                          {String(i).padStart(2, '0')}:00
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Day of Month */}
                <Grid item xs={6} sm={4} md={2.4}>
                  <Tooltip 
                    title={fields.dayOfWeek !== '*' ? 'Haftanın günü seçildiğinde ayın günü seçilemez' : ''}
                    placement="top"
                  >
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ fontSize: '0.875rem' }}>Ayın Günü</InputLabel>
                      <Select
                        value={fields.dayOfMonth}
                        onChange={(e) => handleFieldChange('dayOfMonth', e.target.value)}
                        label="Ayın Günü"
                        disabled={fields.dayOfWeek !== '*'}
                        sx={{
                          background: alpha(theme.palette.background.default, 0.5),
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: fields.dayOfWeek !== '*' 
                              ? alpha(theme.palette.warning.main, 0.3)
                              : alpha(theme.palette.primary.main, 0.2),
                          },
                          opacity: fields.dayOfWeek !== '*' ? 0.6 : 1,
                        }}
                      >
                        <MenuItem value="*">Her gün (*)</MenuItem>
                        {[...Array(31)].map((_, i) => (
                          <MenuItem key={i} value={(i + 1).toString()}>
                            {i + 1}. gün
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Tooltip>
                </Grid>

                {/* Month */}
                <Grid item xs={6} sm={4} md={2.4}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontSize: '0.875rem' }}>Ay</InputLabel>
                    <Select
                      value={fields.month}
                      onChange={(e) => handleFieldChange('month', e.target.value)}
                      label="Ay"
                      sx={{
                        background: alpha(theme.palette.background.default, 0.5),
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: alpha(theme.palette.primary.main, 0.2),
                        },
                      }}
                    >
                      <MenuItem value="*">Her ay (*)</MenuItem>
                      {[
                        'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
                        'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
                      ].map((name, i) => (
                        <MenuItem key={i} value={(i + 1).toString()}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Day of Week */}
                <Grid item xs={6} sm={4} md={2.4}>
                  <Tooltip 
                    title={fields.dayOfMonth !== '*' ? 'Ayın günü seçildiğinde haftanın günü seçilemez' : ''}
                    placement="top"
                  >
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ fontSize: '0.875rem' }}>Haftanın Günü</InputLabel>
                      <Select
                        value={fields.dayOfWeek}
                        onChange={(e) => handleFieldChange('dayOfWeek', e.target.value)}
                        label="Haftanın Günü"
                        disabled={fields.dayOfMonth !== '*'}
                        sx={{
                          background: alpha(theme.palette.background.default, 0.5),
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: fields.dayOfMonth !== '*' 
                              ? alpha(theme.palette.warning.main, 0.3)
                              : alpha(theme.palette.primary.main, 0.2),
                          },
                          opacity: fields.dayOfMonth !== '*' ? 0.6 : 1,
                        }}
                      >
                        <MenuItem value="*">Her gün (*)</MenuItem>
                        {['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'].map(
                          (name, i) => (
                            <MenuItem key={i} value={i.toString()}>
                              {name}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>
                  </Tooltip>
                </Grid>
              </Grid>

              {/* Help Toggle */}
              <Box sx={{ mt: 2 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  onClick={() => setShowHelp(!showHelp)}
                  sx={{ cursor: 'pointer', display: 'inline-flex' }}
                >
                  <Info size={14} color={theme.palette.info.main} />
                  <Typography variant="caption" sx={{ color: theme.palette.info.main }}>
                    Cron formatı nedir?
                  </Typography>
                  <ChevronDown
                    size={14}
                    style={{
                      transform: showHelp ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease',
                    }}
                  />
                </Stack>

                <Collapse in={showHelp}>
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 2,
                      borderRadius: 2,
                      background: alpha(theme.palette.info.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: alpha('#fff', 0.8), display: 'block', mb: 1 }}>
                      Cron formatı: <code style={{ fontFamily: 'monospace' }}>dakika saat gün ay haftanın_günü</code>
                    </Typography>
                    <Stack spacing={0.5}>
                      <Typography variant="caption" sx={{ color: alpha('#fff', 0.7) }}>
                        • <code>*</code> = Her zaman
                      </Typography>
                      <Typography variant="caption" sx={{ color: alpha('#fff', 0.7) }}>
                        • <code>*/5</code> = Her 5 birimde bir
                      </Typography>
                      <Typography variant="caption" sx={{ color: alpha('#fff', 0.7) }}>
                        • <code>0 0 * * *</code> = Her gün gece yarısı
                      </Typography>
                      <Typography variant="caption" sx={{ color: alpha('#fff', 0.7) }}>
                        • <code>0 */6 * * *</code> = Her 6 saatte bir
                      </Typography>
                    </Stack>
                  </Box>
                </Collapse>
              </Box>
            </Box>
          )}

          {/* Manual Mode */}
          {mode === 'manual' && (
            <Box
              component={motion.div}
              key="manual"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <TextField
                fullWidth
                value={internalValue}
                onChange={handleManualChange}
                placeholder="* * * * *"
                error={!!error || !isValid}
                helperText={error || (!isValid && cronValidation.error)}
                InputProps={{
                  startAdornment: (
                    <Hash size={18} color={theme.palette.primary.main} style={{ marginRight: 8 }} />
                  ),
                  sx: {
                    fontFamily: 'monospace',
                    fontSize: '1rem',
                    background: alpha(theme.palette.background.default, 0.5),
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.2),
                    },
                    '&:hover fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.4),
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                }}
              />

              {/* Format Guide */}
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  background: alpha(theme.palette.info.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                <Typography variant="caption" sx={{ color: alpha('#fff', 0.8), display: 'block', mb: 1 }}>
                  Format: <code style={{ fontFamily: 'monospace', fontWeight: 600 }}>dakika saat gün ay haftanın_günü</code>
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6} sm={2.4}>
                    <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                      Dakika (0-59)
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                      Saat (0-23)
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                      Gün (1-31)
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                      Ay (1-12)
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                      Haftanın Günü (0-6)
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </AnimatePresence>

        {/* Current Expression Display */}
        <Box
          sx={{
            mt: 2.5,
            p: 2,
            borderRadius: 2,
            background: alpha(
              isValid ? theme.palette.success.main : theme.palette.error.main,
              0.1
            ),
            border: `1px solid ${alpha(
              isValid ? theme.palette.success.main : theme.palette.error.main,
              0.3
            )}`,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            {isValid ? (
              <CheckCircle size={18} color={theme.palette.success.main} style={{ marginTop: 2 }} />
            ) : (
              <AlertCircle size={18} color={theme.palette.error.main} style={{ marginTop: 2 }} />
            )}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: alpha('#fff', 0.7),
                  display: 'block',
                  mb: 0.5,
                }}
              >
                Cron İfadesi
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  color: isValid ? theme.palette.success.main : theme.palette.error.main,
                  mb: 0.5,
                }}
              >
                {internalValue}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: alpha('#fff', 0.6),
                  display: 'block',
                  fontStyle: 'italic',
                }}
              >
                {getCronDescription(internalValue)}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  )
}

export default ModernCronScheduleEditor